import React from 'react';
import usePowerFlow from '../Provider/PowerFlow';
import useActionDialog from '../Provider/ActionDialogContext';
import RichTextDisplay from './Generic/RichTextDisplay';
import {  trackException } from '../Hooks/withApplicationInsight';

export function useAchievementGeneration(value, onChange) {
    const { aiGenerate } = usePowerFlow();
    const { confirm } = useActionDialog();
    const generate = React.useCallback(async (e) => {
        try {
            const benefit = (value?.mcb_benefit || "").trim().replace(/[.!?]$/, "");
            let achievement = (value?.mcb_achievement || "").trim();
            achievement = (achievement[0]  || "").toLowerCase() + (achievement || "").slice(1);
            let mcb_achievementstatement;
            if(await confirm(
                <AchievementGenerationModal
                    achievement={achievement}
                    benefit={benefit}
                    onChange={(value) => {
                        mcb_achievementstatement = value;
                    }}
                    correctHTML={aiGenerate}
                    remarks={e?.remarks}
                />, { heading: "Sample Achievement Statements."}
            )) {
                onChange((oldValue) => ({...oldValue, mcb_achievementstatement }));
            };
        } catch(error) {
            trackException({error, properties: {
                user: window?.context?.user,
                context: { benefits: value?.mcb_benefit, achievement: value?.mcb_achievement},
                relatedTo: "Achievement Generation",
            } });
            console.error(error);
        }
    }, [value?.mcb_benefit, value?.mcb_achievement, onChange, aiGenerate, confirm]);
    return generate;
}

export default function AchievementGenerationModal({
    achievement, benefit, onChange, correctHTML, remarks
}) {
    const [generatedStatements, setGeneratedStatements] = React.useState([]);
    const [error, setError] = React.useState(null);
    const [achievementStatement, setAchievementStatement] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    React.useEffect(() => {
        let mounted = true;
        setLoading(true);
        correctHTML(`${benefit} by ${achievement}`,
            "Generate 3 distinctly different achievement statement respond in JSON array of string nothing else no markdown no explanations. Format as <p><strong>Benefit, Statistics, and Result</strong> by Task and Action Taken</p>. Keep each  under 500 characters. Use correct grammar and capitalisation in New Zealand/British English with different starting verbs for each statement. "
        ).then((result) => {
            if (!mounted) return;
            setGeneratedStatements(JSON.parse(result));
            setLoading(false);
        }).catch((error) => {  
            trackException({error, properties: {
                context: {achievement, benefit},
                relatedTo: "Achievement Statement Generation",
            }});
            console.error(error);
            if (!mounted) return;
            setError(error);
            setLoading(false);
        });
        return () => { mounted = false; }
    }, [achievement, benefit, correctHTML]);

    React.useEffect(() => {
        if (error){
            const setErrorTimeout = setTimeout(() => {
                setError(null);
            }, 3000);
            return () => clearTimeout(setErrorTimeout);
        }
    }, [error]);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between">
                <div>
                    {remarks && <div>{remarks}</div>}
                    <span>Select one of the options below. You will be able to edit it to best represent your style later.</span>    
                </div>
            </div>
            {loading && <div className="spinner-border text-primary" role="status"/>}
            {error && <div className="alert alert-danger">{error.message}</div>}
            <>
                {generatedStatements.map((statement, index) => (
                    <div key={index} className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="generatedStatement"
                            key={`generatedStatement${index}`}
                            id={`generatedStatement${index}`}
                            value={statement}
                            checked={achievementStatement === statement}
                            onChange={() => {
                                setAchievementStatement(statement);
                                typeof onChange === "function" && onChange(statement);
                            }}
                        />
                        <label className="form-check-label" htmlFor={`generatedStatement${index}`}>
                            <RichTextDisplay value={statement}/>
                        </label>
                    </div>
                ))}
            </>
            <i className="fs-smaller text-center w-100">You are encouraged to review the achievement statement and revise if required. </i>
        </div>
    )
}
